/**
 * Timebox levels in a workflow (design 3.7): the customer's names for periods, top to bottom
 * (Release → Sprint, Phase → Iteration), and the level each type is allocated at.
 */
import { useState, type FormEvent } from "react";
import { useQueryClient } from "@tanstack/react-query";
import type { DefinitionDetail, TimeboxLength } from "@workcore/schemas";
import { api, ApiError } from "../../api";
import { Panel, RowMenu, toast } from "../../ui";
import { confirmImpact } from "../../confirm";
import { InlineText, WriteError, defKey, toKey, useDefinitionWrite } from "./shared";

const UNITS: TimeboxLength["unit"][] = ["day", "week", "month"];

export function LengthInput({ value, onChange, label }: { value: TimeboxLength | null; onChange: (v: TimeboxLength | null) => void; label: string }) {
  return (
    <span className="inline-field length">
      <input
        type="number"
        min={1}
        aria-label={`${label} count`}
        value={value?.count ?? ""}
        placeholder="—"
        onChange={(e) => onChange(e.target.value ? { count: Number(e.target.value), unit: value?.unit ?? "week" } : null)}
      />
      <select aria-label={`${label} unit`} value={value?.unit ?? "week"} onChange={(e) => onChange({ count: value?.count ?? 1, unit: e.target.value as TimeboxLength["unit"] })}>
        {UNITS.map((u) => <option key={u} value={u}>{u}s</option>)}
      </select>
    </span>
  );
}

/** An existing level's length: saved when the number field loses focus or the unit changes, not on each keystroke. */
function SavedLength({ value, onSave, label }: { value: TimeboxLength | null; onSave: (v: TimeboxLength | null) => void; label: string }) {
  const [v, setV] = useState(value);
  const same = (a: TimeboxLength | null) => JSON.stringify(a) === JSON.stringify(value);
  return (
    <span
      onBlur={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget as Node | null) && !same(v)) onSave(v);
      }}
    >
      <LengthInput label={label} value={v} onChange={setV} />
    </span>
  );
}

export function describeLength(l: TimeboxLength | null): string {
  return l ? `${l.count} ${l.unit}${l.count === 1 ? "" : "s"}` : "—";
}

function Levels({ slug, def, canEdit }: { slug: string; def: DefinitionDetail; canEdit: boolean }) {
  const write = useDefinitionWrite(slug, def.id);
  const [label, setLabel] = useState("");
  const [length, setLength] = useState<TimeboxLength | null>(null);
  const [carry, setCarry] = useState(false);
  const levels = [...def.timeboxLevels].sort((a, b) => a.sort - b.sort);
  const add = (e: FormEvent) => {
    e.preventDefault();
    write.mutate(
      { method: "POST", path: `/v1/ws/${slug}/definitions/${def.id}/timebox-levels`, body: { key: toKey(label), label, defaultLength: length, carryOver: carry } },
      { onSuccess: () => (setLabel(""), setLength(null), setCarry(false)) },
    );
  };
  const patch = (id: number, body: unknown) => write.mutate({ method: "PATCH", path: `/v1/ws/${slug}/timebox-levels/${id}`, body });
  return (
    <Panel title="Timebox levels">
      {levels.length === 0 ? (
        <p className="muted">No timeboxes yet. Add a level for each kind of period this workflow plans in: Release and Sprint, or Phase and Iteration, or just Cycle.</p>
      ) : (
        <table className="grid">
          <thead><tr><th>Level</th><th>Plural</th><th>Key</th><th>Usual length</th><th>Unfinished work</th><th /></tr></thead>
          <tbody>
            {levels.map((l, i) => (
              <tr key={l.id}>
                <td>
                  <span className="muted small">{i > 0 ? `${"› ".repeat(i)}` : ""}</span>
                  <InlineText label={`${l.label} label`} value={l.label} disabled={!canEdit} onSave={(v) => patch(l.id, { label: v })} />
                </td>
                <td><InlineText label={`${l.label} plural`} value={l.plural} disabled={!canEdit} onSave={(v) => patch(l.id, { plural: v })} /></td>
                <td><code>{l.key}</code></td>
                <td>{canEdit ? <SavedLength label={`${l.label} usual length`} value={l.defaultLength} onSave={(v) => patch(l.id, { defaultLength: v })} /> : describeLength(l.defaultLength)}</td>
                <td>
                  <label className="check">
                    <input type="checkbox" disabled={!canEdit} checked={l.carryOver} onChange={(e) => patch(l.id, { carryOver: e.target.checked })} />
                    moves to the next {l.label.toLowerCase()} on close
                  </label>
                </td>
                <td className="row-actions">
                  {canEdit && <RowMenu label={`Actions for ${l.label}`} items={[{ label: "Remove…", danger: true, onSelect: () => write.mutate({ method: "DELETE", path: `/v1/ws/${slug}/timebox-levels/${l.id}`, impact: `Remove the '${l.label}' level. Types allocated at it will no longer be allocated.` }) }]} />}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {canEdit && levels.length < 3 && (
        <form autoComplete="off" className="inline wrap" onSubmit={add}>
          <label className="inline-field"><span>{levels.length ? `Inside ${levels.at(-1)!.label}` : "Top level"}</span><input aria-label="New level name" placeholder={levels.length ? "e.g. Sprint" : "e.g. Release"} required value={label} onChange={(e) => setLabel(e.target.value)} /></label>
          <label className="inline-field"><span>Usual length</span><LengthInput label="New level usual length" value={length} onChange={setLength} /></label>
          <label className="check"><input type="checkbox" checked={carry} onChange={(e) => setCarry(e.target.checked)} /> carry unfinished work over</label>
          <button type="submit">Add level</button>
        </form>
      )}
      <p className="muted small">Up to three levels; each one's periods sit inside a period of the level above. Names can change, keys cannot. A level with periods in any project cannot be removed.</p>
      <WriteError error={write.error} />
    </Panel>
  );
}

function Allocation({ slug, def, canEdit }: { slug: string; def: DefinitionDetail; canEdit: boolean }) {
  const q = useQueryClient();
  const [error, setError] = useState<unknown>();
  const levels = [...def.timeboxLevels].sort((a, b) => a.sort - b.sort);
  if (levels.length === 0) return null;
  const set = async (typeId: number, from: number | null, to: number | null) => {
    setError(undefined);
    const path = `/v1/ws/${slug}/types/${typeId}`;
    const body: Record<string, unknown> = { timeboxLevelId: to };
    try {
      try {
        await api("PATCH", path, { body, dryRun: true });
      } catch (e) {
        // allocated items need a decision: clear them, or (moving up) put each in its period's parent
        if (!(e instanceof ApiError) || e.code !== "conflict") throw e;
        const sortOf = (id: number | null) => levels.find((l) => l.id === id)?.sort ?? -1;
        const up = from !== null && to !== null && sortOf(to) < sortOf(from);
        if (!(await confirmImpact(`${e.message.split(";")[0]}. ${up ? "Each will move to the period above the one it is in." : "Their allocation will be cleared."}`))) return;
        body["allocations"] = up ? "parent" : "clear";
        await api("PATCH", path, { body, dryRun: true });
      }
      await api("PATCH", path, { body });
      toast("Saved");
    } catch (e) {
      setError(e);
    } finally {
      void q.invalidateQueries({ queryKey: defKey(slug, def.id) });
    }
  };
  return (
    <Panel title="What goes in which period">
      <table className="grid">
        <thead><tr><th>Type</th><th>Planned in</th></tr></thead>
        <tbody>
          {def.types.map((t) => (
            <tr key={t.id}>
              <td>{t.name}</td>
              <td>
                <select aria-label={`${t.name} planned in`} disabled={!canEdit} value={t.timeboxLevelId ?? ""} onChange={(e) => void set(t.id, t.timeboxLevelId, e.target.value ? Number(e.target.value) : null)}>
                  <option value="">not planned in periods</option>
                  {levels.map((l) => <option key={l.id} value={l.id}>a {l.label.toLowerCase()}</option>)}
                </select>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <p className="muted small">An item in a {levels.at(-1)!.label.toLowerCase()} is also in the periods above it.</p>
      <WriteError error={error} />
    </Panel>
  );
}

export function TimeboxesTab(props: { slug: string; def: DefinitionDetail; canEdit: boolean }) {
  return (
    <>
      <Levels {...props} />
      <Allocation {...props} />
    </>
  );
}
